import { useState } from 'react'
import { formateName } from '@helpers/formateName'
import { Paid } from '..'
import { InputNew } from '@UI/InputNew'
import { useAddAlert } from '@hooks/useAddAlert'
import { directionOrdersApi } from '@api'
import { Booble } from '@common/Boobles/Booble'
import { DatePicker } from 'antd'
import Select from '@UI/New/Select'
import dayjs from 'dayjs'
import { InfoCircleOutlined } from '@ant-design/icons'
import Textarea from '@UI/Textarea'
import TooltipWrapper from '@UI/New/TooltipWrapper/TooltipWrapper'
import s from './index.module.css'
import PrimaryButton from '@pages/Admin/CourseSettings/components/PrimaryButton/PrimaryButton'
import Button from '@UI/New/Button'
import { useSelector } from 'react-redux'
import { normalizeNumberInput } from '@pages/Admin/CourseSettings/components/BasicTextField/helper'

const defaultManualPaymentTypes = ['Оплата наличными', 'Оплата по счету']

const parseManualPaymentTypes = (value) => {
  if (!value) return defaultManualPaymentTypes
  try {
    const parsed = JSON.parse(value)
    if (!Array.isArray(parsed)) return defaultManualPaymentTypes
    const values = parsed
      .map((item) => (typeof item === 'string' ? item : item?.name))
      .map((item) => String(item ?? '').trim())
      .filter(Boolean)
    return [...new Set(values)]
  } catch (e) {
    return defaultManualPaymentTypes
  }
}

const isEnabledSetting = (value) => value === true || String(value) === '1'

const calculateFirstDayMonthProratedAmount = (amount, baseDate) => {
  const date = baseDate ? new Date(baseDate) : new Date()
  const dayOfMonth = Number.isNaN(date.getTime())
    ? new Date().getDate()
    : date.getDate()
  const paidDays = dayOfMonth === 1 ? 30 : Math.max(1, 30 - dayOfMonth)

  return Number(((Number(amount) * paidDays) / 30).toFixed(2))
}

const getInitialPaymentAmount = (data) => {
  if (data?.tariff?.type === 'hour' || data?.tariff?.type === 'minute') {
    return Number(data?.private_balance ?? 0)
  }
  if (data?.tariff?.type === 'group_per_lesson') {
    return Number(data?.payment_amount ?? 0)
  }

  const baseAmount = Number(data?.tariff?.first_price ?? 0)

  if (
    data?.tariff?.type === 'month' &&
    data?.tariff?.renew_to_first_day_of_month
  ) {
    return calculateFirstDayMonthProratedAmount(
      baseAmount,
      data?.next_payment_date,
    )
  }

  return baseAmount
}

const sanitizePaymentAmount = (value, allowNegative = false) => {
  let sanitized = String(value ?? '')
    .replace(/,/g, '.')
    .replace(allowNegative ? /[^0-9.-]/g : /[^\d]/g, '')

  if (!allowNegative) return sanitized

  // Строку не приводим к числу и не подставляем 0 вместо пустого значения:
  // иначе промежуточные «-», «-0.» и пустое поле сбрасываются при вводе.
  const isNegative = sanitized.startsWith('-')
  sanitized = sanitized.replace(/-/g, '')
  sanitized = sanitized.replace(/([.].*)[.]/g, '$1')
  sanitized = sanitized.replace(/^([.])/, '')
  sanitized = sanitized.replace(/([.]\d{2})\d+$/, '$1')

  return `${isNegative ? '-' : ''}${sanitized}`
}

const BALANCE_INPUT_NEW_VALUE = 'new_balance'
const BALANCE_INPUT_PAYMENT_AMOUNT = 'payment_amount'

const ExtendTraining = ({
  data,
  setState,
  closeModal,
  enableBalanceInputMode = false,
}) => {
  const [sum, setSum] = useState(() => getInitialPaymentAmount(data))
  const [balanceInputMode, setBalanceInputMode] = useState(
    BALANCE_INPUT_NEW_VALUE,
  )
  const [loading, setLoading] = useState(false)
  const [comment, setComment] = useState('')
  const [manualPaymentType, setManualPaymentType] = useState(null)
  const [manualRenewalDate, setManualRenewalDate] = useState(dayjs())
  const isManualPaymentTypeSelectionEnabled = useSelector(
    (store) => isEnabledSetting(store.settings.isManualPaymentTypeSelectionEnabled),
  )
  const manualPaymentTypes = parseManualPaymentTypes(
    useSelector((store) => store.settings.custom.manualPaymentTypes),
  )
  const [valData, setData] = useState(
    data?.class_id
      ? data?.class?.classUser?.filter(
          (classUser) =>
            classUser.user_id === data?.children_id &&
            classUser.deleted_at === null,
        )[0]?.next_payment_date
      : data?.next_payment_date ?? new Date().toISOString().slice(0, 10),
  )

  const addAlert = useAddAlert()
  const isPrivateTariff = data.tariff.type === 'hour' || data.tariff.type === 'minute'
  const isGroupPerLessonTariff = data.tariff.type === 'group_per_lesson'
  const showBalanceInputMode =
    enableBalanceInputMode && (isPrivateTariff || isGroupPerLessonTariff)
  // Новый баланс может быть отрицательным (долг), сумма оплаты — нет
  const isNewBalanceInput =
    (isPrivateTariff || isGroupPerLessonTariff) &&
    balanceInputMode === BALANCE_INPUT_NEW_VALUE

  const selectBalanceInputMode = (mode) => {
    if (mode === balanceInputMode) return
    setBalanceInputMode(mode)
    setSum(mode === BALANCE_INPUT_NEW_VALUE ? getInitialPaymentAmount(data) : '')
  }

  const saveClass = () => {
    setLoading(true)
    const formData = new FormData()
    if (data.id != null) {
      formData.append('order_id', String(data.id))
    }
    if (data.class_user_id != null) {
      formData.append('class_user_id', String(data.class_user_id))
    }
    formData.append('class_id', String(data.class_id))
    formData.append('user_id', String(data.children_id))
    formData.append('amount', String(sum))
    formData.append('comment', comment ?? '')
    formData.append(
      'created_at',
      manualRenewalDate?.format('YYYY-MM-DD HH:mm:ss') ?? dayjs().format('YYYY-MM-DD HH:mm:ss'),
    )
    if (manualPaymentType) {
      formData.append('manual_payment_type', manualPaymentType)
    }
    formData.append(
      'next_payment_date',
      String(
        valData ??
          data.class?.classUser?.filter(
            (classUser) =>
              classUser.user_id === data?.children_id &&
              classUser.deleted_at === null,
          )[0]?.next_payment_date ??
          '',
      ),
    )

    directionOrdersApi
      .extendClassByManager({
        data: formData,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      .then((res) => {
        if (res.response?.status >= 400) {
          addAlert({
            type: 'error',
            title:
              'Упс! Ошибка. Попробуйте ещё раз или обратитесь к администратору',
          })
        } else {
          addAlert({ title: 'Обучение продлено!' })

          if (res) {
            setState(res)
          }

          closeModal()
        }
      })
      .finally(() => setLoading(false))
  }

  const saveDirection = () => {
    const normalizedBalance = String(sum ?? '').trim().replace(',', '.')
    const allowNegativeBalance = isNewBalanceInput
    if (
      (isPrivateTariff || isGroupPerLessonTariff) &&
      !(allowNegativeBalance ? /^-?\d+(?:\.\d{1,2})?$/ : /^\d+(?:\.\d{1,2})?$/)
        .test(normalizedBalance)
    ) {
      addAlert({
        type: 'error',
        title: allowNegativeBalance
          ? 'Введите корректное значение баланса'
          : 'Введите корректную неотрицательную сумму',
      })
      return
    }

    setLoading(true)

    const formData = new FormData()
    formData.append(
      'order_id',
      String(data?.newTable ? data?.newTableIdForClass : data.id),
    )
    formData.append(
      'amount',
      isPrivateTariff || isGroupPerLessonTariff
        ? normalizedBalance
        : String(sum),
    )
    if (showBalanceInputMode) {
      formData.append('balance_input_mode', balanceInputMode)
    }
    formData.append('comment', comment ?? '')
    formData.append('manual_payment', '1')
    formData.append(
      'created_at',
      manualRenewalDate?.format('YYYY-MM-DD HH:mm:ss') ?? dayjs().format('YYYY-MM-DD HH:mm:ss'),
    )
    if (manualPaymentType) {
      formData.append('manual_payment_type', manualPaymentType)
    }
    formData.append(
      'currency',
      data?.privateInfo?.currency ?? data?.tariff?.currency ?? 'rub',
    )
    switch (data?.tariff?.type) {
      case 'stream_custom_days':
        formData.append('next_payment_date', String(valData ?? ''))
        break
      case 'stream_custom_period':
        formData.append('next_payment_date', String(valData ?? ''))
        break
      case 'stream_custom_weeks':
        formData.append('next_payment_date', String(valData ?? ''))
        break
      case 'month':
        formData.append('next_payment_date', String(valData ?? ''))
        break
      case 'hour':
        break
      case 'minute':
        break
      default:
        break
      case 'group_per_lesson':
        formData.append('next_payment_date', String(valData ?? ''))
        break
    }

    let type = ''
    if (data?.tariff?.type === 'hour' || data?.tariff?.type === 'minute') {
      type = 'manager-hour'
    } else {
      type = 'manager-month'
    }
    if (data?.newTable) {
      type = 'manager-class'
    }
    if (data?.tariff?.type === 'stream_custom_period') {
      type = 'manager-month'
    }
    if (data?.tariff?.type === 'stream_custom_days') {
      type = 'manager-day'
    }
    if (data?.tariff?.type === 'stream_custom_weeks') {
      type = 'manager-week'
    }

    try {
      directionOrdersApi
        .extendByManager({
          data: formData,
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          params: { type, expand: `orderPayments.order` },
        })
        .then((res) => {
          if (
            res.data.status === 1 ||
            res.data.status === 'success' ||
            res.data.id
          ) {
            addAlert({ title: 'Обучение продлено!' })
            setState(res)

            closeModal()
          } else {
            addAlert({
              type: 'error',
              title:
                'Упс! Ошибка. Попробуйте ещё раз или обратитесь к администратору',
            })
          }
        })
        .catch()
        .finally(() => {
          setLoading(false)
        })
    } catch (error) {
      setLoading(false)
    }
  }

  const save = () => {
    data.class?.one_pay ? saveClass() : saveDirection()
  }

  const onInputDate = (e) => {
    setData(e?.format('YYYY-MM-DD'))
  }

  return (
    <div className={s.wrapper}>
      <h2 className={s.modalTitle}>Продлить обучение</h2>

      <div className={s.colWrap}>
        <Booble className={s.fieldWrapper} text="left">
          {formateName(data.children.name, 'F I')}
        </Booble>
        <Booble className={s.fieldWrapper}>
          <Paid {...{ order: data }} />
        </Booble>
        {data?.tariff?.type === 'group_per_lesson' && (
          <Booble className={s.fieldWrapper}>Группа: {data?.group?.name ?? '-'}</Booble>
        )}
        {data.class_id && (
          <Booble
            className={s.fieldWrapperWithSubtitle}
            text="left"
            subtitle={'Класс'}
          >
            {data.class?.title}
          </Booble>
        )}
        {!data.class?.one_pay === 1 && (
          <Booble
            className={s.fieldWrapper}
            text="left"
            subtitle={data?.tariff?.name}
          >
            {data.direction?.name}
          </Booble>
        )}
        {(data.tariff.type === 'hour' || data.tariff.type === 'minute') && (
          <Booble
            className={s.fieldWrapperWithSubtitle}
            text="left"
            subtitle={`Стоимость занятия (${data?.privateInfo?.default_duration ?? data?.tariff?.lesson_duration ?? 60} минут)`}
          >
            {data?.privateInfo?.hour_cost ?? data?.tariff?.first_price ?? 0}
          </Booble>
        )}
      </div>

      {['month', 'stream_custom_period', 'stream_custom_days', 'stream_custom_weeks']?.includes(
        data.tariff.type,
      ) &&
        data.class_id == null && (
          <>
            <p className={s.descr}>Дата следующего платежа</p>
            <DatePicker
              defaultValue={
                data.next_payment_date == null
                  ? dayjs()
                  : dayjs(data.next_payment_date, 'YYYY-MM-DD')
              }
              onChange={onInputDate}
              format={'DD.MM.YYYY'}
              className={s.dateField}
            />
        {data.class?.one_pay === 1 && (
              <div className={s.infoWrapper}>
                <InfoCircleOutlined className={s.infoIcon} />
                <span className={s.infoWrapperMessage}>
                  Оплата производится для всех направлений по данному классу
                </span>
              </div>
            )}
          </>
        )}

      {data.class_id != null && (
        <>
          <p className={s.descr}>Дата следующего платежа</p>
          <DatePicker
            defaultValue={
              data?.class?.classUser?.find(
                (classUser) =>
                  classUser.user_id === data?.children_id &&
                  classUser.deleted_at === null,
              )?.next_payment_date != undefined
                ? dayjs(
                    data.class.classUser.find(
                      (classUser) =>
                        classUser.user_id === data?.children_id &&
                        classUser.deleted_at === null,
                    ).next_payment_date,
                    'YYYY-MM-DD',
                  )
                : dayjs() // Default to today if null
            }
            onChange={onInputDate}
            format={'DD.MM.YYYY'}
            className={s.dateField}
          />
          {data.class?.one_pay === 1 && (
            <div className={s.infoWrapper}>
              <InfoCircleOutlined className={s.infoIcon} />
              <span className={s.infoWrapperMessage}>
                Оплата производится для всех направлений по данному классу
              </span>
            </div>
          )}
        </>
      )}
      {showBalanceInputMode ? (
        <div className={s.balanceInputModes}>
          <Button
            className={s.balanceInputModeButton}
            color={
              balanceInputMode === BALANCE_INPUT_NEW_VALUE
                ? 'secondary'
                : 'gray'
            }
            variant="action"
            aria-pressed={balanceInputMode === BALANCE_INPUT_NEW_VALUE}
            onClick={() => selectBalanceInputMode(BALANCE_INPUT_NEW_VALUE)}
            rightContent={(
              <TooltipWrapper title="В поле ниже введите новое значение баланса. Например, если у учащегося -500 рублей на балансе, а вы впишете 300, то баланс станет равен 300, и будет записано, что учащийся оплатил 800 рублей">
                <InfoCircleOutlined />
              </TooltipWrapper>
            )}
          >
            Введите значение нового баланса
          </Button>
          <Button
            className={s.balanceInputModeButton}
            color={
              balanceInputMode === BALANCE_INPUT_PAYMENT_AMOUNT
                ? 'secondary'
                : 'gray'
            }
            variant="action"
            aria-pressed={balanceInputMode === BALANCE_INPUT_PAYMENT_AMOUNT}
            onClick={() => selectBalanceInputMode(BALANCE_INPUT_PAYMENT_AMOUNT)}
            rightContent={(
              <TooltipWrapper title="Если вы выбрали данный пункт и вписали в поле ниже, например, 1000 рублей, то это значит, что учащийся оплатил 1000 рублей. И если его баланс был -300 рублей, то новым балансом станет 700">
                <InfoCircleOutlined />
              </TooltipWrapper>
            )}
          >
            Сумма оплаты учащимся
          </Button>
        </div>
      ) : (
        <p className={s.descr}>
          {isPrivateTariff || isGroupPerLessonTariff
            ? 'Введите новое значение баланса'
            : 'Сколько денег заплачено'}
        </p>
      )}
      <InputNew
        className={s.inputField}
        {...{
          value: sum,
          onChange: (e) => setSum(isNewBalanceInput
            ? sanitizePaymentAmount(e.target.value, true)
            : isGroupPerLessonTariff
              ? e.target.value
              : normalizeNumberInput({
                value: e.target.value,
                min: 0,
              })),
          placeholder: '1000 р',
        }}
      />
      <div className={s.descrWithTooltip}>
        <p className={s.descr}>Дата ручного продления</p>
        <TooltipWrapper title="Вы можете выбрать дату и время ручного продления, которое отобразится в отчете оплат.">
          <InfoCircleOutlined className={s.commentTooltipIcon} />
        </TooltipWrapper>
      </div>
      <DatePicker
        showTime={{ format: 'HH:mm' }}
        value={manualRenewalDate}
        onChange={(date) => setManualRenewalDate(date ?? dayjs())}
        format="DD.MM.YYYY HH:mm"
        className={s.dateField}
      />
      {isManualPaymentTypeSelectionEnabled && (
        <>
          <p className={s.descr}>Выберите тип ручного платежа</p>
          <Select
            hideSearchIcon
            wrapperClassName={s.selectFieldWrapper}
            className={s.selectField}
            fields={{
              allowClear: true,
              showSearch: false,
              placeholder: 'Не выбрано',
              value: manualPaymentType,
              onChange: setManualPaymentType,
              options: manualPaymentTypes.map((typeName) => ({
                label: typeName,
                value: typeName,
              })),
            }}
          />
        </>
      )}
      <div className={s.descrWithTooltip}>
        <p className={s.descr}>Комментарий к продлению</p>
        <TooltipWrapper title="Вы можете вписать любой комментарий к продлению, который будет отображаться в отчете оплат.">
          <InfoCircleOutlined className={s.commentTooltipIcon} />
        </TooltipWrapper>
      </div>
      <Textarea
        className={s.textareaField}
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        placeholder="Введите комментарий"
      />

      <div className={s.buttonWrapper}>
        <PrimaryButton isLoading={loading} onClick={save}>
          Сохранить
        </PrimaryButton>
      </div>
    </div>
  )
}

export default ExtendTraining
